import { Timestamp } from 'firebase/firestore';
import type { TicketData, QRGenerationContext, TicketValidation } from '../types/qr-generation';

/**
 * Enterprise Cache Service
 * Provides memory-efficient caching with TTL, size limits, and performance monitoring
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
  size: number;
}

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  maxSize?: number; // Maximum size per entry in bytes
  priority?: 'low' | 'normal' | 'high';
}

interface CacheMetrics {
  hits: number;
  misses: number;
  evictions: number;
  memoryUsage: number;
  entryCount: number;
  hitRate: number;
  averageAccessTime: number;
}

interface CacheConfig {
  maxMemoryMB: number;
  defaultTTL: number;
  cleanupInterval: number;
  maxEntries: number;
  enableMetrics: boolean;
}

/**
 * High-performance LRU cache with TTL and memory management
 */
export class CacheService {
  private cache = new Map<string, CacheEntry<any>>();
  private accessTimes = new Map<string, number>();
  private metrics: CacheMetrics = {
    hits: 0,
    misses: 0,
    evictions: 0,
    memoryUsage: 0,
    entryCount: 0,
    hitRate: 0,
    averageAccessTime: 0
  };

  private readonly config: Required<CacheConfig> = {
    maxMemoryMB: 50, // 50MB cache limit
    defaultTTL: 5 * 60 * 1000, // 5 minutes
    cleanupInterval: 60 * 1000, // 1 minute
    maxEntries: 1000,
    enableMetrics: true
  };

  private cleanupTimer: NodeJS.Timeout | null = null;
  private performanceMonitor: PerformanceObserver | null = null;

  constructor(config?: Partial<CacheConfig>) {
    this.config = { ...this.config, ...config };
    this.startCleanupTimer();
    this.initializePerformanceMonitoring();
  }

  /**
   * Store data in cache with automatic size calculation and TTL
   */
  set<T>(key: string, data: T, options: CacheOptions = {}): boolean {
    const now = Date.now();
    const ttl = options.ttl || this.config.defaultTTL;
    const size = this.calculateDataSize(data);

    // Check size limits
    if (options.maxSize && size > options.maxSize) {
      console.warn(`Cache entry ${key} exceeds max size limit`);
      return false;
    }

    // Check memory limits before adding
    const projectedMemory = this.metrics.memoryUsage + size;
    if (projectedMemory > this.config.maxMemoryMB * 1024 * 1024) {
      this.evictLRU();
    }

    // Check entry count limits
    if (this.cache.size >= this.config.maxEntries) {
      this.evictLRU();
    }

    // Remove existing entry if updating
    if (this.cache.has(key)) {
      const existing = this.cache.get(key)!;
      this.metrics.memoryUsage -= existing.size;
    }

    // Create cache entry
    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      ttl,
      accessCount: 0,
      lastAccessed: now,
      size
    };

    this.cache.set(key, entry);
    this.accessTimes.set(key, now);
    this.metrics.memoryUsage += size;
    this.metrics.entryCount = this.cache.size;

    return true;
  }

  /**
   * Retrieve data from cache with automatic TTL checking
   */
  get<T>(key: string): T | null {
    const start = performance.now();
    const entry = this.cache.get(key);

    if (!entry) {
      this.metrics.misses++;
      this.updateMetrics();
      return null;
    }

    const now = Date.now();

    // Check TTL expiration
    if (now - entry.timestamp > entry.ttl) {
      this.delete(key);
      this.metrics.misses++;
      this.updateMetrics();
      return null;
    }

    // Update access statistics
    entry.accessCount++;
    entry.lastAccessed = now;
    this.accessTimes.set(key, now);

    this.metrics.hits++;
    this.updateMetrics();

    // Track access time for performance monitoring
    if (this.config.enableMetrics) {
      const accessTime = performance.now() - start;
      this.updateAverageAccessTime(accessTime);
    }

    return entry.data as T;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Remove specific key from cache
   */
  delete(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    this.cache.delete(key);
    this.accessTimes.delete(key);
    this.metrics.memoryUsage -= entry.size;
    this.metrics.entryCount = this.cache.size;

    return true;
  }

  /**
   * Get or set pattern - retrieve from cache or compute and store
   */
  async getOrSet<T>(
    key: string,
    computeFn: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<T> {
    // Try to get from cache first
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Compute the value
    const computed = await computeFn();

    // Store in cache
    this.set(key, computed, options);

    return computed;
  }

  /**
   * Evict least recently used entries
   */
  private evictLRU(): void {
    if (this.cache.size === 0) return;

    // Find the least recently used key
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    this.accessTimes.forEach((time, key) => {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.delete(oldestKey);
      this.metrics.evictions++;
    }
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        expiredKeys.push(key);
      }
    });

    for (const key of expiredKeys) {
      this.delete(key);
    }

    if (expiredKeys.length > 0) {
      console.debug(`Cache cleanup: removed ${expiredKeys.length} expired entries`);
    }
  }

  /**
   * Calculate approximate data size in bytes
   */
  private calculateDataSize(data: any): number {
    const json = JSON.stringify(data);
    return new Blob([json]).size;
  }

  /**
   * Update cache metrics
   */
  private updateMetrics(): void {
    const total = this.metrics.hits + this.metrics.misses;
    this.metrics.hitRate = total > 0 ? this.metrics.hits / total : 0;
    this.metrics.entryCount = this.cache.size;
  }

  /**
   * Update average access time
   */
  private updateAverageAccessTime(newTime: number): void {
    const alpha = 0.1; // Exponential moving average factor
    this.metrics.averageAccessTime =
      this.metrics.averageAccessTime * (1 - alpha) + newTime * alpha;
  }

  /**
   * Start periodic cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Initialize performance monitoring
   */
  private initializePerformanceMonitoring(): void {
    if (!this.config.enableMetrics || typeof PerformanceObserver === 'undefined') {
      return;
    }

    try {
      this.performanceMonitor = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        for (const entry of entries) {
          if (entry.name.startsWith('cache-')) {
            this.updateAverageAccessTime(entry.duration);
          }
        }
      });

      this.performanceMonitor.observe({ entryTypes: ['measure'] });
    } catch (error) {
      console.warn('Performance monitoring not available:', error);
    }
  }

  /**
   * Get cache metrics
   */
  getMetrics(): CacheMetrics {
    return { ...this.metrics };
  }

  /**
   * Get cache configuration
   */
  getConfig(): Required<CacheConfig> {
    return { ...this.config };
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.accessTimes.clear();
    this.metrics = {
      hits: 0,
      misses: 0,
      evictions: 0,
      memoryUsage: 0,
      entryCount: 0,
      hitRate: 0,
      averageAccessTime: 0
    };
  }

  /**
   * Resize cache limits
   */
  resize(maxMemoryMB: number, maxEntries: number): void {
    this.config.maxMemoryMB = maxMemoryMB;
    this.config.maxEntries = maxEntries;

    // Evict entries if we're over new limits
    while (this.metrics.memoryUsage > maxMemoryMB * 1024 * 1024 ||
           this.cache.size > maxEntries) {
      this.evictLRU();
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getStats(): {
    size: number;
    memoryUsageMB: number;
    hitRate: number;
    oldestEntry: string | null;
    newestEntry: string | null;
    topKeys: Array<{ key: string; accessCount: number }>;
  } {
    let oldestKey: string | null = null;
    let newestKey: string | null = null;
    let oldestTime = Date.now();
    let newestTime = 0;
    const keyStats: Array<{ key: string; accessCount: number }> = [];

    this.cache.forEach((entry, key) => {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
      if (entry.timestamp > newestTime) {
        newestTime = entry.timestamp;
        newestKey = key;
      }
      keyStats.push({ key, accessCount: entry.accessCount });
    });

    // Sort by access count and take top 10
    keyStats.sort((a, b) => b.accessCount - a.accessCount);
    const topKeys = keyStats.slice(0, 10);

    return {
      size: this.cache.size,
      memoryUsageMB: this.metrics.memoryUsage / (1024 * 1024),
      hitRate: this.metrics.hitRate,
      oldestEntry: oldestKey,
      newestEntry: newestKey,
      topKeys
    };
  }

  /**
   * Destroy cache service and cleanup resources
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }

    if (this.performanceMonitor) {
      this.performanceMonitor.disconnect();
      this.performanceMonitor = null;
    }

    this.clear();
  }
}

/**
 * Specialized cache instances for different data types
 */
export class QRCacheService {
  private cache: CacheService;

  constructor() {
    this.cache = new CacheService({
      maxMemoryMB: 20, // 20MB for QR cache
      defaultTTL: 10 * 60 * 1000, // 10 minutes
      maxEntries: 500,
      enableMetrics: true
    });
  }

  /**
   * Cache ticket data with optimized TTL
   */
  async cacheTicketData(ticketId: string, data: TicketData): Promise<void> {
    const key = `ticket:${ticketId}`;
    this.cache.set(key, data, {
      ttl: 15 * 60 * 1000, // 15 minutes for ticket data
      priority: 'high'
    });
  }

  /**
   * Get cached ticket data
   */
  async getTicketData(ticketId: string): Promise<TicketData | null> {
    const key = `ticket:${ticketId}`;
    return this.cache.get<TicketData>(key);
  }

  /**
   * Cache QR generation result
   */
  async cacheQRGeneration(orderId: string, context: QRGenerationContext): Promise<void> {
    const key = `qr:${orderId}`;
    this.cache.set(key, context, {
      ttl: 30 * 60 * 1000, // 30 minutes for QR generation
      priority: 'normal'
    });
  }

  /**
   * Get cached QR generation
   */
  async getQRGeneration(orderId: string): Promise<QRGenerationContext | null> {
    const key = `qr:${orderId}`;
    return this.cache.get<QRGenerationContext>(key);
  }

  /**
   * Cache ticket validation result temporarily
   */
  async cacheValidationResult(validationId: string, result: TicketValidation): Promise<void> {
    const key = `validation:${validationId}`;
    this.cache.set(key, result, {
      ttl: 5 * 60 * 1000, // 5 minutes for validation results
      priority: 'low'
    });
  }

  /**
   * Get cached validation result
   */
  async getValidationResult(validationId: string): Promise<TicketValidation | null> {
    const key = `validation:${validationId}`;
    return this.cache.get<TicketValidation>(key);
  }

  /**
   * Cache frequently accessed game data
   */
  async cacheGameData(gameId: string, gameData: any): Promise<void> {
    const key = `game:${gameId}`;
    this.cache.set(key, gameData, {
      ttl: 60 * 60 * 1000, // 1 hour for game data
      priority: 'normal'
    });
  }

  /**
   * Get cached game data
   */
  async getGameData(gameId: string): Promise<any | null> {
    const key = `game:${gameId}`;
    return this.cache.get(key);
  }

  /**
   * Invalidate cache for specific order
   */
  async invalidateOrder(orderId: string): Promise<void> {
    const patterns = [`qr:${orderId}`, `order:${orderId}`];
    for (const pattern of patterns) {
      this.cache.delete(pattern);
    }
  }

  /**
   * Invalidate cache for specific ticket
   */
  async invalidateTicket(ticketId: string): Promise<void> {
    this.cache.delete(`ticket:${ticketId}`);
  }

  /**
   * Get cache metrics specific to QR operations
   */
  getQRCacheMetrics(): CacheMetrics & { qrHitRate: number; validationHitRate: number } {
    const baseMetrics = this.cache.getMetrics();

    // Calculate specific hit rates (simplified - in production would track separately)
    return {
      ...baseMetrics,
      qrHitRate: baseMetrics.hitRate, // Simplified
      validationHitRate: baseMetrics.hitRate // Simplified
    };
  }

  /**
   * Warm cache with commonly accessed data
   */
  async warmCache(ticketIds: string[], gameIds: string[]): Promise<void> {
    console.log(`Warming cache with ${ticketIds.length} tickets and ${gameIds.length} games`);

    // This would typically fetch data from Firestore and populate cache
    // Implementation depends on your data access patterns

    for (const ticketId of ticketIds.slice(0, 50)) { // Limit to prevent overload
      // Fetch and cache ticket data
      // const ticketData = await fetchTicketFromFirestore(ticketId);
      // await this.cacheTicketData(ticketId, ticketData);
    }
  }

  /**
   * Cleanup QR cache
   */
  cleanup(): void {
    this.cache.clear();
  }

  /**
   * Get QR cache statistics
   */
  getStats() {
    return this.cache.getStats();
  }

  /**
   * Destroy QR cache service
   */
  destroy(): void {
    this.cache.destroy();
  }
}

// Singleton instances
export const cacheService = new CacheService();
export const qrCacheService = new QRCacheService();

export default CacheService;